
import React, { useState, useEffect } from 'react';
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Clock, Copy, RotateCcw } from "lucide-react";

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Lunes' },
  { key: 'tuesday', label: 'Martes' },
  { key: 'wednesday', label: 'Miércoles' },
  { key: 'thursday', label: 'Jueves' },
  { key: 'friday', label: 'Viernes' },
  { key: 'saturday', label: 'Sábado' },
  { key: 'sunday', label: 'Domingo' }
];

interface DaySchedule {
  enabled: boolean;
  startTime: string;
  endTime: string;
}

interface ScheduleData {
  [key: string]: DaySchedule;
}

interface ScheduleSelectorProps {
  value: string;
  onChange: (schedule: string) => void;
}

export const ScheduleSelector = ({ value, onChange }: ScheduleSelectorProps) => {
  const [scheduleData, setScheduleData] = useState<ScheduleData>({});
  const [isExpanded, setIsExpanded] = useState(false);

  // Parse existing schedule string into structured data
  useEffect(() => {
    const parseScheduleString = (scheduleStr: string): ScheduleData => {
      const initialData: ScheduleData = {};
      
      DAYS_OF_WEEK.forEach(day => {
        initialData[day.key] = {
          enabled: false,
          startTime: '09:00',
          endTime: '10:00'
        };
      });

      if (!scheduleStr) return initialData;

      // Try to parse existing format: "Lunes, Martes 18:00-19:30"
      const parts = scheduleStr.split(' ');
      if (parts.length >= 2) {
        const timePattern = /(\d{2}):(\d{2})-(\d{2}):(\d{2})/;
        const timeMatch = scheduleStr.match(timePattern);
        
        if (timeMatch) {
          const startTime = `${timeMatch[1]}:${timeMatch[2]}`;
          const endTime = `${timeMatch[3]}:${timeMatch[4]}`;
          
          // Extract day names from the beginning of the string
          const daysPart = scheduleStr.split(' ').slice(0, -1).join(' ');
          const cleanDaysPart = daysPart.replace(/,$/, '');
          
          DAYS_OF_WEEK.forEach(day => {
            if (cleanDaysPart.toLowerCase().includes(day.label.toLowerCase())) {
              initialData[day.key] = {
                enabled: true,
                startTime,
                endTime
              };
            }
          });
        }
      }

      return initialData;
    };

    setScheduleData(parseScheduleString(value));
  }, [value]);

  // Generate schedule string from structured data
  const generateScheduleString = (data: ScheduleData): string => {
    const enabledDays = DAYS_OF_WEEK.filter(day => data[day.key]?.enabled);
    
    if (enabledDays.length === 0) return '';

    // Group days with same time
    const timeGroups: { [timeSlot: string]: string[] } = {};
    
    enabledDays.forEach(day => {
      const schedule = data[day.key];
      const timeSlot = `${schedule.startTime}-${schedule.endTime}`;
      
      if (!timeGroups[timeSlot]) {
        timeGroups[timeSlot] = [];
      }
      timeGroups[timeSlot].push(day.label);
    });

    // Generate formatted string
    const timeSlots = Object.keys(timeGroups);
    if (timeSlots.length === 1) {
      const timeSlot = timeSlots[0];
      const daysList = timeGroups[timeSlot].join(', ');
      return `${daysList} ${timeSlot}`;
    }

    // Multiple time slots
    return Object.entries(timeGroups)
      .map(([timeSlot, days]) => `${days.join(', ')} ${timeSlot}`)
      .join(' | ');
  };

  const handleDayToggle = (dayKey: string, enabled: boolean) => {
    const newData = {
      ...scheduleData,
      [dayKey]: {
        ...scheduleData[dayKey],
        enabled
      }
    };
    
    setScheduleData(newData);
    onChange(generateScheduleString(newData));
  };

  const handleTimeChange = (dayKey: string, field: 'startTime' | 'endTime', time: string) => {
    const newData = {
      ...scheduleData,
      [dayKey]: {
        ...scheduleData[dayKey],
        [field]: time
      }
    };
    
    setScheduleData(newData);
    onChange(generateScheduleString(newData));
  };

  const copyTimeToAll = (sourceDay: string) => {
    const sourceSchedule = scheduleData[sourceDay];
    if (!sourceSchedule) return;

    const newData = { ...scheduleData };
    DAYS_OF_WEEK.forEach(day => {
      if (newData[day.key]?.enabled) {
        newData[day.key] = {
          ...newData[day.key],
          startTime: sourceSchedule.startTime,
          endTime: sourceSchedule.endTime
        };
      }
    });

    setScheduleData(newData);
    onChange(generateScheduleString(newData));
  };

  const resetSchedule = () => {
    const resetData: ScheduleData = {};
    DAYS_OF_WEEK.forEach(day => {
      resetData[day.key] = {
        enabled: false,
        startTime: '09:00',
        endTime: '10:00'
      };
    });
    
    setScheduleData(resetData);
    onChange('');
  };

  const enabledDaysCount = DAYS_OF_WEEK.filter(day => scheduleData[day.key]?.enabled).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium text-white/90">
          Horario de la clase
        </Label>
        <div className="flex items-center gap-2">
          {enabledDaysCount > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={resetSchedule}
              className="text-white/70 hover:text-white"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-white/70 hover:text-white"
          >
            <Clock className="h-4 w-4" />
            {enabledDaysCount > 0 ? `${enabledDaysCount} días` : 'Configurar'}
          </Button>
        </div>
      </div>

      {/* Preview of current schedule */}
      {!isExpanded && value && (
        <div className="p-3 bg-black/20 rounded-lg border border-white/10">
          <p className="text-sm text-white/90">{value}</p>
        </div>
      )}

      {/* Expanded schedule selector */}
      {isExpanded && (
        <div className="space-y-3 p-4 bg-black/20 rounded-lg border border-white/10">
          {DAYS_OF_WEEK.map((day) => {
            const daySchedule = scheduleData[day.key] || { enabled: false, startTime: '09:00', endTime: '10:00' };
            
            return (
              <div key={day.key} className="flex items-center gap-4">
                <div className="flex items-center space-x-2 min-w-[100px]">
                  <Checkbox
                    id={day.key}
                    checked={daySchedule.enabled}
                    onCheckedChange={(checked) => handleDayToggle(day.key, !!checked)}
                  />
                  <Label htmlFor={day.key} className="text-sm text-white/90">
                    {day.label}
                  </Label>
                </div>
                
                {daySchedule.enabled && (
                  <div className="flex items-center gap-2 flex-1">
                    <Input
                      type="time"
                      value={daySchedule.startTime}
                      onChange={(e) => handleTimeChange(day.key, 'startTime', e.target.value)}
                      className="w-24 border-white/20 bg-black/30 text-white text-sm"
                    />
                    <span className="text-white/70">-</span>
                    <Input
                      type="time"
                      value={daySchedule.endTime}
                      onChange={(e) => handleTimeChange(day.key, 'endTime', e.target.value)}
                      className="w-24 border-white/20 bg-black/30 text-white text-sm"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => copyTimeToAll(day.key)}
                      className="text-white/70 hover:text-white p-1"
                      title="Copiar horario a todos los días activos"
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
          
          {enabledDaysCount > 0 && (
            <div className="pt-2 border-t border-white/10">
              <p className="text-xs text-white/70">
                Vista previa: {generateScheduleString(scheduleData)}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
